import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
} from '@ads/components-react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef, Row } from '@tanstack/react-table';
import type React from 'react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { DataTable } from '@/shared/ui/organisms/DataTable';
import type { NotificationItem } from '../../../types/notification';
import { NotificationStatusTag } from '../../atoms/NotificationStatusTag';

export interface NotificationTableProps {
  readonly data: readonly NotificationItem[];
  readonly isLoading: boolean;
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly onRowClick: (item: NotificationItem) => void;
  readonly onEdit?: ((item: NotificationItem) => void) | undefined;
  readonly onCancel?: ((item: NotificationItem) => void) | undefined;
  readonly emptyStateTitle?: string;
  readonly emptyStateDescription?: string;
  readonly emptyStateAction?: ReactNode | undefined;
  readonly emptyStateIllustration?: string;
}

const SKELETON_COLS = 7; // número de colunas da tabela

/**
 * Tabela de listagem de notificações.
 *
 * @param data - Lista de notificações da página atual
 * @param isLoading - Estado de carregamento
 * @param totalCount - Total de registros
 * @param currentPage - Página atual (1-based)
 * @param totalPages - Total de páginas
 * @param onPageChange - Callback ao mudar de página
 * @param onRowClick - Callback ao clicar em uma linha
 * @param onEdit - Callback ao clicar em editar (rascunhos/agendadas)
 */
export function NotificationTable({
  data,
  isLoading,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onRowClick,
  onEdit,
  onCancel,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateAction,
  emptyStateIllustration,
}: NotificationTableProps) {
  const { t } = useTranslation('notification');

  const columns = useMemo<ColumnDef<NotificationItem, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        size: 80,
        header: () => <span className="normal-case">{t('table.columns.createdTime')}</span>,
        cell: ({ row }) => (
          <div className="whitespace-nowrap">
            <p className="ts-heading-xxs text-ctx-content-title">
              {row.original.createdAt.split(' ')[0]}
            </p>
            <p className="ts-paragraph-xxs text-ctx-content-base">
              {row.original.createdAt.split(' ')[1]}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'sentAt',
        size: 75,
        header: () => <span className="normal-case">{t('table.columns.dateTime')}</span>,
        cell: ({ row }) => (
          <div className="whitespace-nowrap">
            {row.original.status === 'draft' ? (
              <div className="flex justify-center">
                <p className="ts-heading-xxs text-ctx-content-title">-</p>
              </div>
            ) : (
              <>
                <p className="ts-heading-xxs text-ctx-content-title">
                  {row.original.sentAt.split(' ')[0]}
                </p>
                <p className="ts-paragraph-xxs text-ctx-content-base">
                  {row.original.sentAt.split(' ')[1]}
                </p>
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'title',
        header: () => <span className="normal-case">{t('table.columns.notification')}</span>,
        cell: ({ row }) => (
          <div className="max-w-[220px]">
            <p className="ts-heading-xxs text-ctx-content-title truncate">{row.original.title}</p>
            <p className="ts-paragraph-xxs text-ctx-content-base italic truncate">
              "{row.original.preview}"
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'classrooms',
        size: 130,
        header: () => <span className="normal-case">{t('table.columns.classrooms')}</span>,
        cell: ({ row }) => {
          const classrooms = row.original.classrooms;
          // Mantém a célula com no máximo 2 linhas para uniformizar a altura das
          // linhas: com mais de 2 turmas, exibe 1 nome + indicador de excedente.
          const hasOverflow = classrooms.length > 2;
          const shown = hasOverflow ? classrooms.slice(0, 1) : classrooms.slice(0, 2);
          const overflowCount = classrooms.length - shown.length;
          return (
            <div className="flex flex-col">
              {shown.map(classroom => (
                <p key={classroom} className="ts-paragraph-xxs text-ctx-content-base truncate">
                  {classroom}
                </p>
              ))}
              {overflowCount > 0 && (
                <p className="ts-paragraph-xxs text-ctx-content-base">
                  {t('table.moreClassrooms', { count: overflowCount })}
                </p>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'studentCount',
        size: 50,
        header: () => <span className="normal-case">{t('table.columns.students')}</span>,
        cell: ({ row }) => (
          <p className="ts-paragraph-xxs text-ctx-content-base">{row.original.studentCount}</p>
        ),
      },
      {
        id: 'sender',
        size: 120,
        header: () => <span className="normal-case">{t('table.columns.sentBy')}</span>,
        cell: ({ row }) => (
          <div className="flex items-center gap-nano min-w-none">
            <Avatar size="sm" fallback={row.original.sender.initials} />
            <span className="ts-paragraph-xxs text-ctx-content-base truncate">
              {row.original.sender.name}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        size: 120,
        header: () => <span className="normal-case">{t('table.columns.status')}</span>,
        cell: ({ row }) => <NotificationStatusTag status={row.original.status} />,
      },
      {
        id: 'actions',
        size: 52,
        header: '',
        cell: ({ row }) => {
          const isViewOnly =
            row.original.status === 'delivered' ||
            row.original.status === 'cancelled' ||
            row.original.status === 'processing';

          if (isViewOnly) {
            return (
              <div className="flex justify-end">
                <IconButton
                  icon={EyeIcon}
                  ariaLabel={t('actions.history')}
                  hierarchy="tertiary"
                  size="md"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onRowClick(row.original);
                  }}
                />
              </div>
            );
          }

          return (
            <div className="flex justify-end">
              <Dropdown
                trigger={
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    ariaLabel={t('table.columns.actions')}
                    hierarchy="tertiary"
                    size="md"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                }
                align="end"
              >
                <DropdownItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onRowClick(row.original);
                  }}
                >
                  <DropdownItemContent leadingIcon={EyeIcon}>
                    {t('actions.view')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onEdit?.(row.original);
                  }}
                >
                  <DropdownItemContent leadingIcon={PencilSquareIcon}>
                    {t('actions.edit')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onCancel?.(row.original);
                  }}
                >
                  <DropdownItemContent leadingIcon={XMarkIcon}>
                    {t('actions.cancel')}
                  </DropdownItemContent>
                </DropdownItem>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [t, onEdit, onCancel, onRowClick],
  );

  if (isLoading) {
    return (
      <div className="flex w-full flex-col overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
        <div className="bg-ctx-layout-body px-mini py-xxs">
          <div className="flex gap-mini">
            {Array.from({ length: SKELETON_COLS }).map((_, i) => (
              <Skeleton key={i} className="h-xxs flex-1" />
            ))}
          </div>
        </div>
        <div className="flex flex-col border-t-hairline border-ctx-layout-border">
          {Array.from({ length: pageSize }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-mini border-b-hairline border-ctx-layout-border px-mini py-nano last:border-b-none"
            >
              {Array.from({ length: SKELETON_COLS }).map((_, j) => (
                <div key={j} className="flex flex-1 flex-col gap-micro">
                  <Skeleton className="h-mini w-full" />
                  <Skeleton className="h-mini w-2/3" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const rowClickHandler = (row: Row<NotificationItem>) => onRowClick(row.original);

  return (
    <DataTable
      data={data as NotificationItem[]}
      columns={columns}
      isLoading={false}
      tableFixed
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onRowClick={rowClickHandler}
      emptyStateMessage={emptyStateTitle ?? t('table.empty')}
      {...(emptyStateDescription !== undefined ? { emptyStateDescription } : {})}
      {...(emptyStateAction !== undefined ? { emptyStateAction } : {})}
      {...(emptyStateIllustration !== undefined ? { emptyStateIllustration } : {})}
    />
  );
}
